import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useLanguage } from '../../context/LanguageContext';
import ShubhBottomNav from './ShubhBottomNav';
import { ChevronLeft, ChevronRight, Info, Clock, Calendar } from 'lucide-react';
import '../../styles/shubh-design-system.css';

// Dasha period colors
const DASHA_COLORS = {
  sun: { bg: '#FFF3E0', accent: '#FFB74D', border: '#FFB74D' },
  moon: { bg: '#E3F2FD', accent: '#64B5F6', border: '#64B5F6' },
  mars: { bg: '#FFEBEE', accent: '#EF5350', border: '#EF5350' },
  mercury: { bg: '#E8F5E9', accent: '#66BB6A', border: '#66BB6A' },
  jupiter: { bg: '#FFF8E1', accent: '#F5B942', border: '#F5B942' },
  venus: { bg: '#FCE4EC', accent: '#EC407A', border: '#EC407A' },
  saturn: { bg: '#E8EAF6', accent: '#7986CB', border: '#7986CB' },
  rahu: { bg: '#ECEFF1', accent: '#78909C', border: '#78909C' },
  ketu: { bg: '#EFEBE9', accent: '#A1887F', border: '#A1887F' },
};

const ShubhDashaTimelinePage = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const isHindi = language === 'hi';
  const [activeSystem, setActiveSystem] = useState('vimshottari');
  const [expandedDasha, setExpandedDasha] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dashaData, setDashaData] = useState([]);

  const systems = [
    { id: 'vimshottari', label: isHindi ? 'विंशोत्तरी' : 'Vimshottari' },
    { id: 'ashtottari', label: isHindi ? 'अष्टोत्तरी' : 'Ashtottari' },
    { id: 'yogini', label: isHindi ? 'योगिनी' : 'Yogini' },
  ];

  // Planet names mapping
  const planetNames = {
    sun: { en: 'Sun', hi: 'सूर्य', symbol: '☉' },
    moon: { en: 'Moon', hi: 'चंद्र', symbol: '☽' },
    mars: { en: 'Mars', hi: 'मंगल', symbol: '♂' },
    mercury: { en: 'Mercury', hi: 'बुध', symbol: '☿' },
    jupiter: { en: 'Jupiter', hi: 'बृहस्पति', symbol: '♃' },
    venus: { en: 'Venus', hi: 'शुक्र', symbol: '♀' },
    saturn: { en: 'Saturn', hi: 'शनि', symbol: '♄' },
    rahu: { en: 'Rahu', hi: 'राहु', symbol: '☊' },
    ketu: { en: 'Ketu', hi: 'केतु', symbol: '☋' },
  };

  useEffect(() => {
    // Simulated dasha data
    setTimeout(() => {
      setDashaData([
        {
          planet: 'jupiter',
          startDate: '2019-04-15',
          endDate: '2035-04-15',
          duration: '16 years',
          isCurrent: true,
          antardashas: [
            { planet: 'jupiter', start: '2019-04-15', end: '2021-06-03', duration: '2y 1m 18d' },
            { planet: 'saturn', start: '2021-06-03', end: '2024-06-15', duration: '2y 6m 12d' },
            { planet: 'mercury', start: '2024-06-15', end: '2026-09-21', duration: '2y 3m 6d', isCurrent: true },
            { planet: 'ketu', start: '2026-09-21', end: '2027-08-28', duration: '11m 7d' },
            { planet: 'venus', start: '2027-08-28', end: '2030-04-28', duration: '2y 8m' },
            { planet: 'sun', start: '2030-04-28', end: '2031-02-16', duration: '9m 19d' },
            { planet: 'moon', start: '2031-02-16', end: '2032-06-16', duration: '1y 4m' },
            { planet: 'mars', start: '2032-06-16', end: '2033-05-23', duration: '11m 7d' },
            { planet: 'rahu', start: '2033-05-23', end: '2035-04-15', duration: '2y 4m 23d' },
          ]
        },
        {
          planet: 'saturn',
          startDate: '2035-04-15',
          endDate: '2054-04-15',
          duration: '19 years',
          isCurrent: false,
        },
        {
          planet: 'mercury',
          startDate: '2054-04-15',
          endDate: '2071-04-15',
          duration: '17 years',
          isCurrent: false,
        },
      ]);
      setLoading(false);
    }, 500);
  }, []);

  const getPlanetName = (key) => isHindi ? planetNames[key]?.hi : planetNames[key]?.en;
  const getPlanetSymbol = (key) => planetNames[key]?.symbol;

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(isHindi ? 'hi-IN' : 'en-IN', { 
      month: 'short', 
      year: 'numeric' 
    });
  };

  return (
    <div className="shubh-page" style={{ background: 'var(--shubh-ivory)' }}>
      {/* Header */}
      <header 
        className="sticky top-0 z-20 px-4 py-3"
        style={{ 
          background: 'var(--shubh-ivory)',
          borderBottom: '1px solid var(--border-light)',
          paddingTop: 'calc(12px + env(safe-area-inset-top))'
        }}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => navigate('/janam-kundli')}
              className="p-2 rounded-full"
              style={{ background: 'var(--warm-beige)' }}
            >
              <ChevronLeft className="w-5 h-5" style={{ color: 'var(--ink-dark)' }} />
            </button>
            <h1 
              className="text-lg font-medium"
              style={{ fontFamily: 'var(--font-display)', color: 'var(--ink-dark)' }}
            >
              {isHindi ? 'दशा समयरेखा' : 'Dasha Timeline'}
            </h1>
          </div>
          <button 
            className="p-2 rounded-full"
            style={{ background: 'var(--warm-beige)' }}
          >
            <Info className="w-5 h-5" style={{ color: 'var(--ink-muted)' }} />
          </button>
        </div>
      </header>

      <div className="shubh-page-content" style={{ paddingTop: 'var(--space-4)' }}>
        {/* System Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <div className="shubh-segmented">
            {systems.map((system) => (
              <button
                key={system.id}
                onClick={() => setActiveSystem(system.id)}
                className={`shubh-segment flex-1 ${activeSystem === system.id ? 'active' : ''}`}
              >
                {system.label}
              </button>
            ))}
          </div>
        </motion.div>

        {/* Current Dasha Highlight */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="shubh-featured-card mb-6"
        >
          <div className="flex items-center gap-2 mb-2">
            <Clock className="w-4 h-4" style={{ color: 'var(--saffron-dark)' }} />
            <span className="text-sm font-medium" style={{ color: 'var(--ink-dark)' }}>
              {isHindi ? 'वर्तमान दशा' : 'Current Period'}
            </span>
          </div>
          
          <div className="flex items-center gap-4">
            <div 
              className="w-14 h-14 rounded-2xl flex items-center justify-center text-2xl"
              style={{ background: DASHA_COLORS.jupiter.bg }}
            >
              ♃
            </div>
            <div>
              <h3 className="text-lg font-medium" style={{ color: 'var(--ink-dark)' }}>
                {isHindi ? 'बृहस्पति महादशा' : 'Jupiter Mahadasha'}
              </h3>
              <p className="text-sm" style={{ color: 'var(--ink-muted)' }}>
                {isHindi ? 'बुध अंतर्दशा' : 'Mercury Antardasha'}
              </p>
              <p className="text-xs mt-1" style={{ color: 'var(--vermilion)' }}>
                Jun 2024 – Sep 2026
              </p>
            </div>
          </div>
        </motion.div>

        {/* Mahadasha List */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.15 }}
        >
          <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--ink-dark)' }}>
            {isHindi ? 'महादशा अवधि' : 'Mahadasha Periods'}
          </h3>

          <div className="space-y-3">
            {loading ? (
              Array(3).fill(0).map((_, i) => (
                <div key={i} className="shubh-skeleton h-20 rounded-xl" />
              ))
            ) : (
              dashaData.map((dasha, index) => {
                const colors = DASHA_COLORS[dasha.planet];
                const isExpanded = expandedDasha === dasha.planet;
                
                return (
                  <motion.div
                    key={dasha.planet}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <button
                      onClick={() => setExpandedDasha(isExpanded ? null : dasha.planet)}
                      className={`shubh-card w-full text-left p-4 ${dasha.isCurrent ? 'ring-2' : ''}`}
                      style={{ 
                        borderLeft: `4px solid ${colors.border}`,
                        ringColor: dasha.isCurrent ? 'var(--saffron)' : 'transparent'
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div 
                            className="w-10 h-10 rounded-full flex items-center justify-center text-lg"
                            style={{ background: colors.bg }}
                          >
                            {getPlanetSymbol(dasha.planet)}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <h4 className="font-medium" style={{ color: 'var(--ink-dark)' }}>
                                {getPlanetName(dasha.planet)} {isHindi ? 'महादशा' : 'Mahadasha'}
                              </h4>
                              {dasha.isCurrent && (
                                <span 
                                  className="text-[10px] px-2 py-0.5 rounded-full font-medium"
                                  style={{ background: 'rgba(200, 75, 50, 0.1)', color: 'var(--vermilion)' }}
                                >
                                  {isHindi ? 'वर्तमान' : 'Current'}
                                </span>
                              )}
                            </div>
                            <p className="text-xs" style={{ color: 'var(--ink-muted)' }}>
                              {formatDate(dasha.startDate)} – {formatDate(dasha.endDate)} • {dasha.duration}
                            </p>
                          </div>
                        </div>
                        
                        <ChevronRight 
                          className={`w-5 h-5 transition-transform ${isExpanded ? 'rotate-90' : ''}`} 
                          style={{ color: 'var(--ink-muted)' }} 
                        />
                      </div>
                    </button>

                    {/* Antardasha Expansion */}
                    {isExpanded && dasha.antardashas && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="mt-2 ml-4 border-l-2 pl-4 space-y-2"
                        style={{ borderColor: 'var(--border-light)' }}
                      >
                        {dasha.antardashas.map((ad, adIndex) => {
                          const adColors = DASHA_COLORS[ad.planet];
                          return (
                            <div 
                              key={adIndex}
                              className={`p-3 rounded-xl ${ad.isCurrent ? 'ring-1' : ''}`}
                              style={{ 
                                background: ad.isCurrent ? adColors.bg : 'var(--pure-white)',
                                ringColor: ad.isCurrent ? adColors.accent : 'transparent'
                              }}
                            >
                              <div className="flex items-center gap-2">
                                <span className="text-base">{getPlanetSymbol(ad.planet)}</span>
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm font-medium" style={{ color: 'var(--ink-dark)' }}>
                                      {getPlanetName(ad.planet)}
                                    </span>
                                    {ad.isCurrent && (
                                      <span 
                                        className="text-[9px] px-1.5 py-0.5 rounded font-medium"
                                        style={{ background: 'rgba(200, 75, 50, 0.15)', color: 'var(--vermilion)' }}
                                      >
                                        {isHindi ? 'अभी' : 'Now'}
                                      </span>
                                    )}
                                  </div>
                                  <span className="text-xs" style={{ color: 'var(--ink-muted)' }}>
                                    {formatDate(ad.start)} – {formatDate(ad.end)}
                                  </span>
                                </div>
                                <span className="text-xs" style={{ color: 'var(--ink-light)' }}>
                                  {ad.duration}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </motion.div>
                    )}
                  </motion.div>
                );
              })
            )}
          </div>
        </motion.div>

        {/* Info Card */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mt-6 p-4 rounded-xl"
          style={{ background: 'var(--warm-beige)' }}
        >
          <div className="flex items-start gap-3">
            <Calendar className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: 'var(--saffron-dark)' }} />
            <div>
              <p className="text-sm font-medium mb-1" style={{ color: 'var(--ink-dark)' }}>
                {isHindi ? 'विंशोत्तरी दशा के बारे में' : 'About Vimshottari Dasha'}
              </p>
              <p className="text-xs" style={{ color: 'var(--ink-light)' }}>
                {isHindi 
                  ? 'विंशोत्तरी दशा 120 वर्षों की अवधि को 9 ग्रहों में विभाजित करती है। प्रत्येक ग्रह की अवधि में उस ग्रह का प्रभाव प्रबल होता है।'
                  : 'Vimshottari Dasha divides a 120-year cycle among 9 planets. Each planetary period brings the influence of that planet to the forefront.'
                }
              </p>
            </div>
          </div>
        </motion.div>
      </div>

      <ShubhBottomNav />
    </div>
  );
};

export default ShubhDashaTimelinePage;
